import React from 'react'
import { debounce } from 'lodash'

import { monetize } from '../../lib/formats'
import DisplayField from '../UI/DisplayField'
import ValidatedTextInput from '../UI/ValidatedTextInput'
import BooleanSelect from '../UI/BooleanSelect'
import SmallDeleteButton from '../UI/SmallDeleteButton'
import { ServiceHeader } from './QuoteFormComponents'
import './styles/line-item-input.css'

/*
Props:
- lineItem object
- service object
- quoteId: id of the corresponding quote
- validatedOnChange: callback function for onChange, but including a
  validation flag and field name:
  callback(lineItem, Bool(isValid), this.props.name || 'lineItem')
- deleteCallback: callback function for the delete button. Button is only shown
  if this is present.
  callback(event, lineItemId)
*/
class LineItemInput extends React.PureComponent {
  constructor(props) {
    super(props)
    const attributes = this.props.lineItem.attributes
    const serviceAttrs = this.props.service.attributes
    // Rate is an annyoing attribute.
    // If it's null or undefined we use the service rate
    // else we should use whatever value is inside - including zero
    const rate =
      attributes.rate === null || attributes.rate === undefined
        ? serviceAttrs.rate
        : attributes.rate
    this.state = {
      error: '',
      lineItemId: this.props.lineItem.id,
      total: attributes.total || 0.0,
      event: attributes.event,
      description: attributes.description || '',
      number: (attributes.number === null) ? 1 : attributes.number,
      rate: rate,
      rateIncludingAdjustment: attributes.rateIncludingAdjustment,
      rateUnit: attributes.rateUnit || serviceAttrs.rateUnit,
      formattedRate: serviceAttrs.formattedRate,
      formattedRateIncludingAdjustment: serviceAttrs.formattedRateIncludingAdjustment,
      adjustment: attributes.adjustment || '0.00',
      serviceId: attributes.serviceId || serviceAttrs.id,
      service: this.props.service,
      serviceRate: serviceAttrs.rate,
      cost: attributes.cost || serviceAttrs.cost,
      formattedCost: attributes.formattedCost || serviceAttrs.formattedCost,
      hideClientRates: Boolean(attributes.hideClientRates)
    }

    // Debouncing this._validatedOnChange() for performance
    this._validatedOnChange = debounce(this._validatedOnChange, 50)
  }

  componentWillMount() {
    this._updateFields('number')
  }

  _change = (value, valid, attribute) => {
    let updates = {}
    updates[attribute] = value
    this.setState(updates, () => this._updateFields(attribute))
  }

  _delete = ev => {
    if (!this.props.deleteCallback) return
    this.props.deleteCallback(ev, this.state.lineItemId)
  }

  _getRate = () =>
    String(this.state.rate) === ''
      ? Number(this.state.serviceRate)
      : Number(this.state.rate)

  _updateFields = updatedAttribute => {
    if (updatedAttribute.match(/number|rate|adjustment/)) {
      const total =
        Number(this.state.number) * this._getRate() +
        Number(this.state.adjustment)
      this.setState({ total: total }, this._validatedOnChange)
    } else {
      this._validatedOnChange()
    }
  }

  _validatedOnChange = () => {
    if (!this.props.validatedOnChange) return
    const attributes = {
      quoteId: this.props.quoteId,
      serviceId: this.state.serviceId,
      description: this.state.description,
      event: this.state.event,
      number: this.state.number,
      rate: this._getRate(),
      cost: this.state.cost,
      adjustment: this.state.adjustment,
      total: this.state.total,
      hideClientRates: this.state.hideClientRates
    }
    this.props.validatedOnChange(
      attributes,
      true,
      this.props.name || 'lineItem'
    )
  }

  render() {
    const { deleteCallback } = this.props
    const {
      service,
      description,
      number,
      rate,
      serviceRate,
      adjustment,
      total,
      hideClientRates
    } = this.state

    return (
      <main className="line-item-input">
        <ServiceHeader service={service} />

        <section className="input-area">
          <section className="main-container">
            <div className="row">
              <div className="small">
                <BooleanSelect
                  name="hideClientRates"
                  floatingLabelText="Show only total to client?"
                  value={hideClientRates}
                  onChange={(ev, index, value) =>
                    this._change(value, true, 'hideClientRates')
                  }
                  fullWidth
                />
              </div>

              <div className="tiny">
                <ValidatedTextInput
                  name="number"
                  value={(number === 0) ? '0' : (number || 1)}
                  floatingLabelText="Qty"
                  pattern={/^(\d+)\.?(\d+)?$/}
                  errorMessage="Invalid"
                  validatedOnChange={this._change}
                  alignRight
                  fullWidth
                  immediateValidation
                />
              </div>

              <div className="tiny">
                <ValidatedTextInput
                  name="rate"
                  value={rate === 0 ? '0.00' : rate}
                  floatingLabelText="Rate"
                  hintText={serviceRate}
                  pattern={/^(\d+)\.?(\d+)?$/}
                  errorMessage="Invalid"
                  validatedOnChange={this._change}
                  alignRight
                  fullWidth
                  immediateValidation
                />
              </div>

              <div className="tiny">
                <ValidatedTextInput
                  name="adjustment"
                  value={adjustment}
                  floatingLabelText="Adjustment"
                  pattern={/^-?(\d+)\.?(\d+)?$/}
                  errorMessage="Invalid"
                  validatedOnChange={this._change}
                  alignRight
                  fullWidth
                  immediateValidation
                />
              </div>
            </div>

            <div className="row">
              <div className="full-width">
                <ValidatedTextInput
                  name="description"
                  value={description || service.attributes.name}
                  floatingLabelText="Client-facing service description (leave blank to use service name)"
                  validatedOnChange={this._change}
                  multiLine
                  rows={2}
                  rowsMax={15}
                  fullWidth
                />
              </div>
            </div>
          </section>

          <section className="total-container">
            <div className="total">
              <DisplayField
                name="total"
                value={monetize(total)}
                floatingLabelText="Line total"
                alignRight
                fullWidth
              />
            </div>

            {deleteCallback && (
              <div className="delete">
                <SmallDeleteButton
                  onTouchTap={deleteCallback}
                  style={{ marginTop: '26px' }}
                />
              </div>
            )}
          </section>
        </section>
      </main>
    )
  }
}

export default LineItemInput



// WEBPACK FOOTER //
// ./src/components/Quotes/LineItemInput.jsx